import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDSAlert, IDSTabs, IDSTab, IDSTabPanel } from "@inera/ids-react";
import ReportFilterPanel from "./ReportFilterPanel";
import ReportAccordionList from "./ReportAccordionList";
import {
  fetchOrganisationTestExecutionSummaries,
  fetchTestExecutionSummaries,
} from "../../services/testExecutionAPI";
import { useOrganisation } from "../../contexts/OrganisationContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  REVIEWER_TAB_IDS,
  REVIEWER_TAB_LABELS,
  partitionReviewerReports,
} from "../../utils/reviewerReportTabs";

/** Empty states per reviewer queue, so an empty tab says which queue is empty. */
const REVIEWER_TAB_EMPTY_TEXTS = {
  AWAITING: "Inga rapporter att granska.",
  ASSIGNED: "Inga pågående granskningar.",
  MINE: "Du har inga granskningar i Mina granskningar.",
  FINISHED: "Inga slutförda granskningar.",
};

/**
 * Layout component for displaying all saved test reports.
 * Includes filtering by service contract, sorting by date,
 * and actions such as export to PDF.
 *
 * Only report metadata is fetched here. The questionnaire sections, answers, comments and test
 * results belong to the review page and are loaded there for the single report being opened —
 * fetching them for every report in the list meant one request per section per report, plus a
 * full test-result payload each, without any of it being shown.
 */
function TestReportsLayout({ variant = "tester" }) {
  const [reports, setReports] = useState([]);
  const [selectedContractFilter, setSelectedContractFilter] = useState("Alla");
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { activeOrgId } = useOrganisation();
  const { loading: authLoading, isAuthenticated, user } = useAuth();
  const isReviewer = variant === "reviewer";

  // Role-specific texts.
  const heading = isReviewer ? "Testrapporter att granska" : "Organisationens testrapporter";
  const emptyText = isReviewer
    ? "Inga rapporter att granska."
    : "Din organisation har inga inskickade testrapporter ännu.";

  // Extract loadReports function so it can be called on mount and on status change
  const loadReports = async () => {
    try {
      setLoading(true);

      // Reviewers review other organisations' submissions, so scoping the lookup to their own
      // organisation would return nothing. The listing endpoint decides what they may see from
      // their organisation membership. Testers instead see every submitted report in their own
      // organisation, not just their own.
      const summaries = isReviewer
        ? await fetchTestExecutionSummaries(activeOrgId)
        : await fetchOrganisationTestExecutionSummaries(activeOrgId);

      // Drafts are already excluded by the summary endpoints, so everything here is a
      // formally submitted report.
      setReports(
        (summaries || []).map((summary) => ({
          ...summary,
          // Older rows predate the submittedAt column, so fall back to executedAt for those.
          date: summary.submittedAt || summary.executedAt,
          selectedContract: summary.contractName,
        }))
      );
    } catch (err) {
      console.error("Error loading test reports:", err);
      setError(err.message || "Något gick fel. Försök igen.");
    } finally {
      setLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    // Wait for authentication and organisation context to be ready
    if (authLoading || !isAuthenticated || !activeOrgId) {
      console.log('Waiting for auth and org context...', { authLoading, isAuthenticated, activeOrgId });
      return;
    }

    loadReports();
  }, [activeOrgId, authLoading, isAuthenticated]);

  // Callback to refetch after status change
  const handleStatusChange = () => {
    loadReports();
  };

  const contracts = Array.from(new Set(reports.map((r) => r.selectedContract))).sort();

  const filteredReports = reports
    .filter(
      (report) =>
        selectedContractFilter === "Alla" ||
        report.selectedContract === selectedContractFilter
    )
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const renderList = (rows, emptyMessage) =>
    rows.length === 0 ? (
      <p className="ids-mt-4">{emptyMessage}</p>
    ) : (
      <ReportAccordionList
        filteredReports={rows}
        navigate={navigate}
        onStatusChange={handleStatusChange}
        isReviewer={isReviewer}
      />
    );

  // Reviewers work through four queues, so their list is split per queue instead of
  // showing every organisation's reports in one undifferentiated list.
  const reviewerTabs = isReviewer
    ? partitionReviewerReports(filteredReports, user?.id)
    : null;

  return (
    <div  className="ids-mt-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: '1200px', margin: '2rem auto' }}>
        <h1 className="ids-heading-l">{heading}</h1>

        {loading && <p className="ids-mt-4">Laddar rapporter...</p>}
        {error && (
          <IDSAlert type="error" className="ids-mt-4">
            <p>Kunde inte ladda rapporterna. Försök ladda om sidan.</p>
          </IDSAlert>
        )}

        {!loading && !error && (
          <>
            <ReportFilterPanel
              contracts={contracts}
              selectedContractFilter={selectedContractFilter}
              setSelectedContractFilter={setSelectedContractFilter}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />

            {isReviewer ? (
              <div className="ids-mt-6">
                <IDSTabs
                  breakpoint="s"
                  tabs={REVIEWER_TAB_IDS.map((tabId) => (
                    <IDSTab
                      key={tabId}
                      label={`${REVIEWER_TAB_LABELS[tabId]} (${reviewerTabs[tabId].length})`}
                    />
                  ))}
                >
                  {REVIEWER_TAB_IDS.map((tabId) => (
                    <IDSTabPanel key={tabId} noFocus>
                      {renderList(reviewerTabs[tabId], REVIEWER_TAB_EMPTY_TEXTS[tabId])}
                    </IDSTabPanel>
                  ))}
                </IDSTabs>
              </div>
            ) : (
              renderList(filteredReports, emptyText)
            )}
          </>
        )}
      </main>
    </div>
  );
}


export default TestReportsLayout;
